import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { formatMoney, currencySymbol } from "@/lib/currency";
import { CurrencySelect } from "@/components/CurrencySelect";
import { SupplierPicker } from "@/components/LineSupplierPicker";

// New Purchase Order form, also used to review each PO read by
// "Import PO" (Tally PO PDF / Excel / CSV → POST /purchase-orders/import/preview)
// before it is created with POST /purchase-orders (backend/po_import.py).

const blankLine = () => ({
  key: Math.random().toString(36).slice(2), item_text: "", item_id: null, create_item: false,
  new_code: "", new_name: "", qty: "", uom: "", unit_cost: "", discount_pct: "", due_date: "", description: "", suggestions: [],
  supplier_id: "", // the user's pick for this part; "" = follow the BOM / PO supplier
});

const emptyDraft = () => ({
  supplier_id: "", supplier_mode: "existing", new_supplier: { name: "", gstin: "", email: "", phone: "", address: "", state: "", payment_terms: "" },
  use_file_po_no: false, external_po_no: "", po_date: new Date().toISOString().slice(0, 10), expected_receipt_date: "",
  payment_terms: "", reference_no: "", other_references: "", dispatched_through: "", destination: "", terms_of_delivery: "",
  gst_pct: "18", currency: "INR", notes: "", source: "manual", import_file_name: "", warnings: [], duplicate_of: null, allow_duplicate: false,
  send_to_supplier: false, lines: [blankLine()],
});

const itemLabel = (it) => `${it.code} — ${it.name}`;

// Map an import preview draft (backend shape) onto the form's shape.
export function draftFromPreview(d) {
  const sm = d.supplier_match || {};
  return {
    ...emptyDraft(),
    source: "import",
    import_file_name: d.file_name || "",
    supplier_id: sm.supplier_id || "",
    supplier_mode: sm.supplier_id ? "existing" : (d.supplier?.name ? "new" : "existing"),
    supplier_suggestions: sm.suggestions || [],
    new_supplier: { ...emptyDraft().new_supplier, ...(d.supplier || {}), payment_terms: d.payment_terms || "" },
    external_po_no: d.external_po_no || "",
    po_date: d.po_date || emptyDraft().po_date,
    expected_receipt_date: d.expected_receipt_date || "",
    payment_terms: d.payment_terms || "",
    reference_no: d.reference_no || "",
    other_references: d.other_references || "",
    dispatched_through: d.dispatched_through || "",
    destination: d.destination || "",
    terms_of_delivery: d.terms_of_delivery || "",
    gst_pct: d.gst_pct != null ? String(d.gst_pct) : "",
    currency: d.currency || "INR",
    notes: d.notes || "",
    warnings: d.warnings || [],
    duplicate_of: d.duplicate_of || null,
    file_total: d.total,
    lines: (d.lines || []).map((l) => ({
      ...blankLine(),
      item_id: l.item_id || null,
      item_text: l.item_id ? `${l.item_code} — ${l.item_name}` : "",
      create_item: !l.item_id,
      new_code: l.item_code && !l.item_id ? l.item_code : "",
      new_name: l.description || "",
      qty: l.qty ?? "", uom: l.uom || "", unit_cost: l.unit_cost ?? "", discount_pct: l.discount_pct || "",
      due_date: l.due_date || "", description: [l.description, ...(l.spec || [])].filter(Boolean).join(" · "),
      suggestions: l.suggestions || [], match: l.match,
    })),
  };
}

export default function PurchaseOrderEditor({ open, onClose, onCreated, initial, items, suppliers, stepLabel }) {
  const [f, setF] = useState(emptyDraft());
  const [busy, setBusy] = useState(false);
  const [bomSup, setBomSup] = useState({}); // item_id → suppliers linked to the part in the BOM

  useEffect(() => { if (open) setF(initial || emptyDraft()); }, [open, initial]);

  const lineItemIds = f.lines.map((l) => l.item_id).filter(Boolean);
  const itemIdsKey = [...new Set(lineItemIds)].sort().join(",");
  useEffect(() => {
    if (!open || !itemIdsKey) return;
    let live = true;
    api.get("/auto-procurement/bom-suppliers", { params: { item_ids: itemIdsKey } })
      .then(({ data }) => live && setBomSup((p) => ({ ...p, ...(data || {}) })))
      .catch(() => {});
    return () => { live = false; };
  }, [open, itemIdsKey]);

  const itemsByLabel = useMemo(() => Object.fromEntries(items.map((i) => [itemLabel(i).toLowerCase(), i])), [items]);
  const itemsByCode = useMemo(() => Object.fromEntries(items.map((i) => [String(i.code).toLowerCase(), i])), [items]);
  const itemsById = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const activeSuppliers = useMemo(() => suppliers.filter((s) => s.status !== "blacklisted"), [suppliers]);
  const supplierById = useMemo(() => Object.fromEntries(suppliers.map((s) => [s.id, s])), [suppliers]);

  // Supplier for a line: the user's pick, else the part's only BOM supplier,
  // else the PO's supplier when it is one of the part's BOM suppliers (or the
  // part has none). mode: bom_single | bom_conflict | bom_multiple | any.
  const lineSupplier = (l) => {
    const cands = (l.item_id && bomSup[l.item_id]) || [];
    const header = f.supplier_mode === "existing" ? f.supplier_id : "";
    let mode = cands.length === 1 ? "bom_single" : cands.length ? "bom_multiple" : "any";
    if (mode === "bom_single" && header && cands[0].id !== header) mode = "bom_conflict";
    const options = mode === "bom_conflict" && supplierById[header] ? [...cands, supplierById[header]] : cands;
    const auto = mode === "bom_single" ? cands[0].id
      : mode === "any" || cands.some((c) => c.id === header) ? header : "";
    return { mode, options, id: l.supplier_id || auto, picked: !!l.supplier_id };
  };

  // A PO with no supplier yet takes the one its lines agree on.
  useEffect(() => {
    if (!open || f.supplier_mode !== "existing" || f.supplier_id) return;
    const ids = [...new Set(f.lines.filter((l) => l.item_id).map((l) => lineSupplier(l).id).filter(Boolean))];
    if (ids.length === 1 && supplierById[ids[0]]) {
      const s = supplierById[ids[0]];
      setF((p) => ({ ...p, supplier_id: s.id, payment_terms: p.payment_terms || s.payment_terms || "" }));
    }
  }, [open, bomSup, f.lines, f.supplier_id, f.supplier_mode, supplierById]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setLine = (key, patch) => setF((p) => ({ ...p, lines: p.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)) }));
  const onItemText = (l, text) => {
    const t = text.trim().toLowerCase();
    const hit = itemsByLabel[t] || itemsByCode[t] || itemsByCode[t.split(" — ")[0]];
    setLine(l.key, { item_text: text, item_id: hit ? hit.id : null, uom: hit && !l.uom ? (hit.uom || "") : l.uom, create_item: hit ? false : l.create_item });
  };
  const pickSuggestion = (l, s) => setLine(l.key, { item_id: s.item_id, item_text: `${s.code} — ${s.name}`, create_item: false });

  const amount = (l) => (Number(l.qty) || 0) * (Number(l.unit_cost) || 0) * (1 - (Number(l.discount_pct) || 0) / 100);
  const subtotal = f.lines.reduce((s, l) => s + amount(l), 0);
  const gst = subtotal * (Number(f.gst_pct) || 0) / 100;

  const save = async () => {
    const lines = [];
    for (const [i, l] of f.lines.entries()) {
      if (!l.item_id && !(l.create_item && l.new_code.trim())) return toast.error(`Line ${i + 1}: pick an item, or tick "create item" and give it a code`);
      if (!(Number(l.qty) > 0)) return toast.error(`Line ${i + 1}: quantity must be more than 0`);
      const ls = l.item_id && f.supplier_mode === "existing" ? lineSupplier(l) : null;
      if (ls && !ls.id) return toast.error(`Line ${i + 1}: select the supplier for this part`);
      if (ls && f.supplier_id && ls.id !== f.supplier_id) {
        return toast.error(`Line ${i + 1}: its supplier (${supplierById[ls.id]?.name || "?"}) differs from the PO's supplier — one PO goes to one supplier`);
      }
      lines.push({
        supplier_id: ls ? ls.id : null,
        supplier_source: ls ? (ls.mode === "bom_single" && !ls.picked ? "bom_auto" : "user_selected") : "",
        item_id: l.item_id || null,
        new_item: !l.item_id ? { code: l.new_code.trim(), name: (l.new_name || l.new_code).trim(), uom: l.uom || "PCS" } : null,
        qty: Number(l.qty), unit_cost: Number(l.unit_cost) || 0, discount_pct: Number(l.discount_pct) || 0,
        uom: l.uom, description: l.description, due_date: l.due_date || null,
      });
    }
    if (f.supplier_mode === "existing" && !f.supplier_id) return toast.error("Select a supplier");
    if (f.supplier_mode === "new" && !f.new_supplier.name.trim()) return toast.error("Enter the new supplier's name");
    const body = {
      supplier_id: f.supplier_mode === "existing" ? f.supplier_id : null,
      new_supplier: f.supplier_mode === "new" ? f.new_supplier : null,
      code: f.use_file_po_no && f.external_po_no ? f.external_po_no : null,
      external_po_no: f.external_po_no, po_date: f.po_date || null, expected_receipt_date: f.expected_receipt_date || null,
      payment_terms: f.payment_terms, reference_no: f.reference_no, other_references: f.other_references,
      dispatched_through: f.dispatched_through, destination: f.destination, terms_of_delivery: f.terms_of_delivery,
      gst_pct: f.gst_pct === "" ? null : Number(f.gst_pct), currency: f.currency || "INR", notes: f.notes, source: f.source,
      import_file_name: f.import_file_name, allow_duplicate: !!f.allow_duplicate, send_to_supplier: !!f.send_to_supplier, lines,
    };
    setBusy(true);
    try {
      const { data } = await api.post("/purchase-orders", body);
      const d = data.dispatch;
      toast.success(`${data.purchase_order.code} created${d ? ` · email ${d.email}, WhatsApp ${d.whatsapp}` : ""}`);
      onCreated?.(data.purchase_order);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not create the purchase order");
    } finally { setBusy(false); }
  };

  const field = (k, label, props = {}) => (
    <div><Label className="text-xs">{label}</Label><Input value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`po-form-${k}`} {...props} /></div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="po-editor">
        <DialogHeader>
          <DialogTitle>
            {f.source === "import" ? "Review imported Purchase Order" : "New Purchase Order"}
            {stepLabel ? <span className="ml-2 text-xs font-normal text-slate-500">{stepLabel}</span> : null}
          </DialogTitle>
        </DialogHeader>

        {f.source === "import" && (
          <div className="text-xs text-slate-500">From <span className="mono">{f.import_file_name}</span>{f.file_total ? ` · file total ${formatMoney(f.file_total, f.currency)}` : ""}</div>
        )}
        {(f.warnings || []).length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-xs px-3 py-2 space-y-0.5" data-testid="po-import-warnings">
            {f.warnings.map((w, i) => <div key={i} className="flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{w}</div>)}
          </div>
        )}

        {/* Supplier */}
        <div className="space-y-2">
          <div className="flex items-center gap-4 text-sm">
            <span className="font-semibold text-slate-700">Supplier</span>
            <label className="flex items-center gap-1.5"><input type="radio" checked={f.supplier_mode === "existing"} onChange={() => set("supplier_mode", "existing")} /> From Supplier Master</label>
            <label className="flex items-center gap-1.5"><input type="radio" checked={f.supplier_mode === "new"} onChange={() => set("supplier_mode", "new")} data-testid="po-form-new-supplier" /> New supplier</label>
          </div>
          {f.supplier_mode === "existing" ? (
            <div className="space-y-1">
              <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-sm bg-white" value={f.supplier_id}
                onChange={(e) => {
                  const s = suppliers.find((x) => x.id === e.target.value);
                  setF((p) => ({ ...p, supplier_id: e.target.value, payment_terms: p.payment_terms || s?.payment_terms || "" }));
                }} data-testid="po-form-supplier">
                <option value="">— Select supplier —</option>
                {activeSuppliers.map((s) => <option key={s.id} value={s.id}>{s.code} — {s.name}{s.status !== "active" ? ` (${s.status})` : ""}</option>)}
              </select>
              {!f.supplier_id && (f.supplier_suggestions || []).length > 0 && (
                <div className="text-xs text-slate-500">Did you mean:{" "}
                  {f.supplier_suggestions.map((s) => (
                    <button key={s.supplier_id} type="button" className="text-blue-600 hover:underline mr-2" onClick={() => set("supplier_id", s.supplier_id)}>{s.name}</button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {["name", "gstin", "email", "phone", "state", "payment_terms"].map((k) => (
                <div key={k} className={k === "name" ? "md:col-span-2" : ""}>
                  <Label className="text-xs capitalize">{k.replace("_", " ")}</Label>
                  <Input value={f.new_supplier[k] || ""} onChange={(e) => set("new_supplier", { ...f.new_supplier, [k]: e.target.value })} data-testid={`po-form-ns-${k}`} />
                </div>
              ))}
              <div className="col-span-2 md:col-span-4">
                <Label className="text-xs">Address</Label>
                <Textarea rows={2} value={f.new_supplier.address || ""} onChange={(e) => set("new_supplier", { ...f.new_supplier, address: e.target.value })} />
              </div>
            </div>
          )}
        </div>

        {/* Header fields (Tally PO layout) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {field("external_po_no", "Supplier / Tally PO No.")}
          {field("po_date", "PO Date", { type: "date" })}
          {field("expected_receipt_date", "Expected Delivery", { type: "date" })}
          {field("payment_terms", "Mode / Terms of Payment")}
          {field("reference_no", "Reference No.")}
          {field("other_references", "Other References")}
          {field("dispatched_through", "Dispatched through")}
          {field("destination", "Destination")}
          {field("terms_of_delivery", "Terms of Delivery")}
          {field("gst_pct", "GST %", { type: "number", min: 0, max: 100 })}
          <CurrencySelect label={<span className="text-xs">Currency</span>} value={f.currency} onChange={(v) => set("currency", v)} testid="po-form-currency" />
          <div className="md:col-span-2 flex items-end">
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={!!f.use_file_po_no} disabled={!f.external_po_no} onChange={(e) => set("use_file_po_no", e.target.checked)} data-testid="po-form-use-file-no" />
              Use this PO No. as the ERP PO number (otherwise the next PO-xxxx)
            </label>
          </div>
        </div>

        {/* Lines */}
        <datalist id="po-editor-items">
          {items.map((i) => <option key={i.id} value={itemLabel(i)} />)}
        </datalist>
        <div className="border border-slate-200 rounded-md overflow-x-auto">
          <table className="w-full text-sm" data-testid="po-form-lines">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="py-2 px-2 w-8">#</th><th className="py-2 px-2 min-w-[260px]">Item</th><th className="py-2 px-2 w-24">Qty</th>
                <th className="py-2 px-2 w-20">UoM</th><th className="py-2 px-2 w-24">Rate ({currencySymbol(f.currency)})</th><th className="py-2 px-2 w-16">Disc %</th>
                <th className="py-2 px-2 w-36">Due on</th><th className="py-2 px-2 w-28 text-right">Amount</th><th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {f.lines.map((l, idx) => (
                <tr key={l.key} className={`border-t border-slate-100 align-top ${!l.item_id && !l.create_item ? "bg-amber-50/50" : ""}`} data-testid={`po-form-line-${idx}`}>
                  <td className="py-2 px-2 text-slate-500">{idx + 1}</td>
                  <td className="py-2 px-2 space-y-1">
                    <Input list="po-editor-items" placeholder="Search item code or name…" value={l.item_text} onChange={(e) => onItemText(l, e.target.value)} data-testid={`po-form-item-${idx}`} />
                    {l.description && <div className="text-[11px] text-slate-500">{l.description}</div>}
                    {l.item_id && f.supplier_mode === "existing" && (() => {
                      const ls = lineSupplier(l);
                      if (ls.mode === "bom_single") {
                        return <div className="text-[11px] text-slate-600" data-testid={`po-form-line-supplier-${idx}`}>Supplier (BOM): {supplierById[ls.id]?.name || ls.options[0]?.name}</div>;
                      }
                      return (
                        <div className="text-[11px]" data-testid={`po-form-line-supplier-${idx}`}>
                          <div className="text-amber-700">
                            {ls.mode === "bom_multiple" ? "Several BOM suppliers — pick one"
                              : ls.mode === "bom_conflict" ? "BOM supplier differs from the PO's — pick one"
                                : "No supplier in BOM — pick from Supplier Master"}
                          </div>
                          <SupplierPicker value={ls.id} onChange={(v) => setLine(l.key, { supplier_id: v })}
                            options={ls.mode === "any" ? activeSuppliers : ls.options} searchable={ls.mode === "any"}
                            testid={`po-form-line-supplier-select-${idx}`} />
                        </div>
                      );
                    })()}
                    {l.item_id && itemsById[l.item_id] && l.match && <div className="text-[10px] text-emerald-700">Matched by {l.match}</div>}
                    {!l.item_id && l.suggestions.length > 0 && (
                      <div className="text-[11px] text-slate-500">Suggestions:{" "}
                        {l.suggestions.map((s) => (
                          <button key={s.item_id} type="button" className="text-blue-600 hover:underline mr-2" onClick={() => pickSuggestion(l, s)}>{s.code}</button>
                        ))}
                      </div>
                    )}
                    {!l.item_id && (
                      <div className="space-y-1">
                        <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
                          <input type="checkbox" checked={!!l.create_item} onChange={(e) => setLine(l.key, { create_item: e.target.checked })} data-testid={`po-form-create-item-${idx}`} />
                          Not in Item Master — create item
                        </label>
                        {l.create_item && (
                          <div className="grid grid-cols-2 gap-1">
                            <Input className="h-8" placeholder="New item code" value={l.new_code} onChange={(e) => setLine(l.key, { new_code: e.target.value })} data-testid={`po-form-new-code-${idx}`} />
                            <Input className="h-8" placeholder="Item name" value={l.new_name} onChange={(e) => setLine(l.key, { new_name: e.target.value })} />
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-2"><Input type="number" min="0" value={l.qty} onChange={(e) => setLine(l.key, { qty: e.target.value })} data-testid={`po-form-qty-${idx}`} /></td>
                  <td className="py-2 px-2"><Input value={l.uom} onChange={(e) => setLine(l.key, { uom: e.target.value.toUpperCase() })} /></td>
                  <td className="py-2 px-2"><Input type="number" min="0" value={l.unit_cost} onChange={(e) => setLine(l.key, { unit_cost: e.target.value })} data-testid={`po-form-rate-${idx}`} /></td>
                  <td className="py-2 px-2"><Input type="number" min="0" max="100" value={l.discount_pct} onChange={(e) => setLine(l.key, { discount_pct: e.target.value })} /></td>
                  <td className="py-2 px-2"><Input type="date" value={l.due_date} onChange={(e) => setLine(l.key, { due_date: e.target.value })} /></td>
                  <td className="py-2 px-2 text-right mono">{amount(l).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                  <td className="py-2 px-1">
                    {f.lines.length > 1 && (
                      <Button variant="ghost" size="icon" onClick={() => setF((p) => ({ ...p, lines: p.lines.filter((x) => x.key !== l.key) }))}><Trash2 className="w-4 h-4 text-slate-400" /></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setF((p) => ({ ...p, lines: [...p.lines, blankLine()] }))} data-testid="po-form-add-line">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add line
          </Button>
          <div className="text-sm text-right space-y-0.5" data-testid="po-form-totals">
            <div>Sub-total <span className="mono ml-3">{formatMoney(subtotal, f.currency)}</span></div>
            <div className="text-slate-500">GST {f.gst_pct || 0}% <span className="mono ml-3">{formatMoney(gst, f.currency)}</span></div>
            <div className="font-semibold">Total <span className="mono ml-3" data-testid="po-form-grand-total">{formatMoney(subtotal + gst, f.currency, { withCode: true })}</span></div>
          </div>
        </div>

        <div><Label className="text-xs">Notes</Label><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></div>
        <div className="flex flex-wrap gap-4 text-xs text-slate-600">
          {f.duplicate_of && (
            <label className="flex items-center gap-1.5 text-amber-800">
              <input type="checkbox" checked={!!f.allow_duplicate} onChange={(e) => set("allow_duplicate", e.target.checked)} data-testid="po-form-allow-dup" />
              Already imported as {f.duplicate_of} — create again anyway
            </label>
          )}
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={!!f.send_to_supplier} onChange={(e) => set("send_to_supplier", e.target.checked)} data-testid="po-form-send" />
            Send the PO to the supplier now (email + WhatsApp, with follow-ups)
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="po-form-cancel">{stepLabel ? "Skip" : "Cancel"}</Button>
          <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="po-form-save">{busy ? "Creating…" : "Create Purchase Order"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
